import { BaseMessage } from './BaseMessage.js';
import AnyField from './fields/AnyField.js';
import ArrayField from './fields/ArrayField.js';
import BaseField from './fields/BaseField.js';
import BooleanField from './fields/BooleanField.js';
import ConstField from './fields/ConstField.js';
import DateField from './fields/DateField.js';
import EnumField from './fields/EnumField.js';
import MessageField from './fields/MessageField.js';
import NumberField from './fields/NumberField.js';
import OneOfField from './fields/OneOfField.js';
import RecordField from './fields/RecordField.js';
import StringField from './fields/StringField.js';
import MessageDescriptor from './MessageDescriptor.js';
const Message = (newMetadata = {}) => (target) => MessageDescriptor.load(target).addMessageMetadata(newMetadata);
const getDefaultValue = (target, fieldName, expectedType) => {
    const targetCtor = target instanceof BaseMessage
        ? target.constructor
        : target;
    const defaultInstance = targetCtor.create({});
    const defaultValue = defaultInstance[fieldName];
    if (defaultValue === undefined) {
        return undefined;
    }
    let isValid = true;
    if (typeof expectedType === 'string') {
        isValid = typeof defaultValue === expectedType;
    }
    else {
        isValid = defaultValue instanceof expectedType;
    }
    if (!isValid) {
        throw new TypeError(`Field '${fieldName}' in class '${defaultInstance.constructor.name}' was defined as '${expectedType}' but had default value of type '${typeof defaultValue}'`);
    }
    return defaultValue;
};
const defineField = (field) => (target, fieldName) => {
    let fieldInstance;
    if (field instanceof BaseField) {
        fieldInstance = field;
    }
    else {
        switch (field.type) {
            case 'boolean':
                field.default = getDefaultValue(target, fieldName, 'boolean');
                fieldInstance = new BooleanField(field);
                break;
            case 'number':
                field.default = getDefaultValue(target, fieldName, 'number');
                fieldInstance = new NumberField(field);
                break;
            case 'string':
                field.default = getDefaultValue(target, fieldName, 'string');
                fieldInstance = new StringField(field);
                break;
            case 'enum':
                field.default = getDefaultValue(target, fieldName, 'string');
                fieldInstance = new EnumField(field);
                break;
            case 'date':
                field.default = getDefaultValue(target, fieldName, Date);
                fieldInstance = new DateField(field);
                break;
            case 'any':
                fieldInstance = new AnyField(field);
                break;
            case 'array':
                fieldInstance = new ArrayField(field);
                break;
            case 'record':
                fieldInstance = new RecordField(field);
                break;
            case 'oneOf':
                fieldInstance = new OneOfField(field);
                break;
            case 'message':
                fieldInstance = new MessageField({
                    ...field,
                    message: Reflect.getMetadata('design:type', target, fieldName),
                });
                break;
            case 'const':
                {
                    const value = getDefaultValue(target, fieldName, 'string');
                    if (value === undefined) {
                        throw new Error(`Const field '${fieldName}' in class must be a string, 'readonly' and defined.`);
                    }
                    fieldInstance = new ConstField({ const: value, required: true });
                }
                break;
            default:
                throw new Error(`Invalid field type ${field.type}`);
        }
    }
    MessageDescriptor.load(target).addFieldMetadata(fieldName, fieldInstance);
};
const Field = {
    any: (metadata = {}) => defineField({ type: 'any', ...metadata }),
    boolean: (metadata = {}) => defineField({ type: 'boolean', ...metadata }),
    string: (metadata = {}) => defineField({ type: 'string', ...metadata }),
    date: (metadata = {}) => defineField({ type: 'date', ...metadata }),
    number: (metadata) => defineField({ type: 'number', ...metadata }),
    enum: (metadata) => defineField({ type: 'enum', ...metadata }),
    oneOf: (metadata) => defineField({ type: 'oneOf', ...metadata }),
    array: (metadata) => defineField({ type: 'array', ...metadata }),
    record: (metadata) => defineField({
        type: 'record',
        ...metadata,
    }),
    const: (metadata = {}) => defineField({ type: 'const', ...metadata }),
    message: (metadata = {}) => defineField({ type: 'message', ...metadata }),
    default: (value) => value,
};
export { Field, Message };
//# sourceMappingURL=decorators.js.map